import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import {
  Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Loader2, Paperclip, X, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { CATEGORIES } from "@/lib/complaints";

const ComplaintNew = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const fileRef = useRef(null);
  const prefilledListing = params.get("listing_id") || "";
  const prefilledRespondent = params.get("respondent_id") || "";
  const prefilledPayment = params.get("payment_id") || "";
  const [listingTitle, setListingTitle] = useState("");
  const [respondentName, setRespondentName] = useState("");

  const [form, setForm] = useState({
    category: "",
    subject: "",
    description: "",
    respondent_email: "",
    respondent_id: prefilledRespondent,
    listing_id: prefilledListing,
    payment_id: prefilledPayment,
  });
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (prefilledListing) {
      api.get(`/listings/${prefilledListing}`).then((r) => {
        setListingTitle(r.data.title || "");
        if (!form.respondent_id && r.data.owner_id) {
          setForm((f) => ({ ...f, respondent_id: r.data.owner_id }));
          setRespondentName(r.data.owner_name || "");
        }
      }).catch(() => {});
    }
  }, [prefilledListing]); // eslint-disable-line react-hooks/exhaustive-deps

  const uploadEvidence = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setAttachments((a) => [...a, { path: data.path, name: file.name, content_type: file.type }]);
      toast.success("Evidence uploaded");
    } catch (e) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.category) return toast.error("Pick a category");
    if (form.subject.trim().length < 4) return toast.error("Subject too short");
    if (form.description.trim().length < 10) return toast.error("Tell us a bit more (10+ characters)");
    setSubmitting(true);
    try {
      const payload = {
        category: form.category,
        subject: form.subject.trim(),
        description: form.description.trim(),
        attachments,
      };
      if (form.respondent_id) payload.respondent_id = form.respondent_id;
      else if (form.respondent_email) payload.respondent_email = form.respondent_email.toLowerCase();
      if (form.listing_id) payload.listing_id = form.listing_id;
      if (form.payment_id) payload.payment_id = form.payment_id;
      const { data } = await api.post("/complaints", payload);
      toast.success("Case opened — we'll notify the other party.");
      navigate(`/complaints/${data.complaint_id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not file complaint");
    } finally {
      setSubmitting(false);
    }
  };

  if (!user) return null;

  // Group categories for the Select
  const groups = CATEGORIES.reduce((acc, c) => {
    (acc[c.group] = acc[c.group] || []).push(c);
    return acc;
  }, {});

  return (
    <div className="container-narrow py-10 max-w-3xl">
      <div className="label-overline text-[#C05A3C] mb-2">Resolution centre</div>
      <h1 className="font-display text-4xl mb-3">File a complaint</h1>
      <p className="text-muted-foreground font-sans-ui mb-8 max-w-2xl">
        Tell us what happened. If this is about a paid order, we&apos;ll automatically hold the seller&apos;s
        payout while we review. Be specific — attach receipts, screenshots or photos.
      </p>

      <form onSubmit={submit} className="space-y-6" data-testid="complaint-form">
        <Card className="p-6 bg-white border-black/10 space-y-5">
          <div>
            <Label className="label-overline">Category</Label>
            <Select
              value={form.category}
              onValueChange={(v) => setForm({ ...form, category: v })}
            >
              <SelectTrigger className="mt-2" data-testid="complaint-category"><SelectValue placeholder="What's this about?" /></SelectTrigger>
              <SelectContent>
                {Object.entries(groups).map(([group, items]) => (
                  <SelectGroup key={group}>
                    <SelectLabel>{group}</SelectLabel>
                    {items.map((c) => (
                      <SelectItem key={c.value} value={c.value} data-testid={`cat-${c.value}`}>{c.label}</SelectItem>
                    ))}
                  </SelectGroup>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="label-overline">Subject</Label>
            <Input
              data-testid="complaint-subject"
              value={form.subject}
              onChange={(e) => setForm({ ...form, subject: e.target.value })}
              placeholder="One-line summary, e.g. 'Order #ABC never arrived'"
              className="mt-2"
              maxLength={160}
            />
          </div>

          <div>
            <Label className="label-overline">What happened?</Label>
            <Textarea
              data-testid="complaint-description"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={6}
              placeholder="Dates, amounts, what was promised vs delivered, attempts to resolve, etc."
              className="mt-2"
              maxLength={5000}
            />
            <div className="text-xs text-muted-foreground mt-1">{form.description.length}/5000</div>
          </div>

          {(prefilledListing || form.listing_id) && (
            <div className="bg-[#F3EFE9] rounded-md p-3 text-sm flex items-center gap-2">
              <ShieldAlert className="w-4 h-4 text-[#C05A3C]" />
              Linked to listing <span className="font-semibold">{listingTitle || form.listing_id}</span>
            </div>
          )}
          {prefilledPayment && (
            <div className="bg-[#1A2024] text-[#D9923B] rounded-md p-3 text-xs">
              ⚠ Linked to payment <span className="font-mono">{prefilledPayment.slice(0, 24)}…</span> — payout will be auto-held.
            </div>
          )}

          {!prefilledRespondent && !prefilledListing && (
            <div>
              <Label className="label-overline">Who are you reporting? (optional)</Label>
              <Input
                data-testid="complaint-respondent-email"
                value={form.respondent_email}
                onChange={(e) => setForm({ ...form, respondent_email: e.target.value })}
                placeholder="seller@example.com — leave blank for general feedback"
                className="mt-2"
                type="email"
              />
              <div className="text-xs text-muted-foreground mt-1">Leave blank if you can&apos;t name a specific person.</div>
            </div>
          )}
          {respondentName && (
            <div className="text-xs text-muted-foreground">Reporting: <b>{respondentName}</b></div>
          )}
        </Card>

        <Card className="p-6 bg-white border-black/10">
          <Label className="label-overline">Evidence (optional)</Label>
          <p className="text-xs text-muted-foreground mt-1">Screenshots, photos, receipts. PDF / image, max 8MB each.</p>
          <div className="mt-4 flex flex-wrap gap-2">
            {attachments.map((a, i) => (
              <div key={i} className="flex items-center gap-2 bg-[#F3EFE9] rounded-md px-3 py-1.5 text-xs" data-testid={`attachment-${i}`}>
                <Paperclip className="w-3 h-3" />
                <span className="truncate max-w-[180px]">{a.name}</span>
                <button type="button" onClick={() => setAttachments((arr) => arr.filter((_, j) => j !== i))}>
                  <X className="w-3 h-3 text-muted-foreground hover:text-[#C05A3C]" />
                </button>
              </div>
            ))}
            <Button
              type="button"
              onClick={() => fileRef.current?.click()}
              variant="outline"
              disabled={uploading}
              data-testid="upload-evidence-btn"
              className="rounded-md text-xs"
            >
              {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Paperclip className="w-3.5 h-3.5 mr-1" />}
              Add file
            </Button>
            <input
              ref={fileRef}
              type="file"
              hidden
              accept="image/*,application/pdf"
              onChange={(e) => e.target.files?.[0] && uploadEvidence(e.target.files[0])}
              data-testid="complaint-file-input"
            />
          </div>
        </Card>

        <div className="flex items-center gap-3">
          <Button
            type="submit"
            disabled={submitting}
            className="bg-[#C05A3C] hover:bg-[#D46B4E] text-white rounded-md"
            data-testid="submit-complaint-btn"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ShieldAlert className="w-4 h-4 mr-2" />}
            Open case
          </Button>
          <Link to="/complaints" className="text-sm text-muted-foreground underline">Cancel</Link>
        </div>
      </form>
    </div>
  );
};

export default ComplaintNew;
